import { getSupabaseBrowserClient } from 'web/lib/supabase/client'
import { getSourceUrl, Notification } from 'common/notification'
import {
  handlePushNotificationPermissionStatus,
  setPushToken,
} from 'web/lib/supabase/notifications'
import { useRouter } from 'next/router'
import {
  setInstalledAppPlatform,
  setIsNativeOld,
} from 'web/lib/native/is-native'
import { useNativeMessages } from 'web/hooks/use-native-messages'
import { createContext, useContext, useEffect } from 'react'
import { usePrivateUser } from 'web/hooks/use-user'
import { useEvent } from 'client-common/hooks/use-event'
import { postMessageToNative } from 'web/lib/native/post-message'
import { MesageTypeMap, nativeToWebMessageType } from 'common/native-message'
import { usePersistentLocalState } from 'web/hooks/use-persistent-local-state'
import { api } from 'web/lib/api/api'

type NativeContextType = {
  isNative: boolean
  platform: string
  version: string
  isIOS: boolean
}

export const NativeContext = createContext<NativeContextType | undefined>(
  undefined
)

export const NativeMessageProvider = (props: { children: React.ReactNode }) => {
  const { children } = props
  const router = useRouter()
  const privateUser = usePrivateUser()
  const [isNative, setIsNative] = usePersistentLocalState(false, 'is-native-v2')
  const [platform, setPlatform] = usePersistentLocalState(
    '',
    'native-platform-v2'
  )
  const [version, setVersion] = usePersistentLocalState('', 'native-version')

  useEffect(() => {
    postMessageToNative('startedListening', {})
  }, [])

  useEffect(() => {
    postMessageToNative('versionRequested', {})
  }, [])

  useNativeMessages(['version'], (type, data) => {
    const { version } = data
    console.log('Native version', version)
    if (version) {
      setVersion(version)
    }
  })

  useEffect(() => {
    const { nativePlatform } = router.query
    if (nativePlatform !== undefined) {
      const platform = nativePlatform as string
      setIsNativeOld(true, platform)
      setIsNative(true)
      setPlatform(platform)
      if (privateUser) setInstalledAppPlatform(privateUser, platform)
    }
  }, [privateUser, router.query])

  const handleNativeMessage = useEvent(
    async (type: nativeToWebMessageType, data: MesageTypeMap[typeof type]) => {
      if (type === 'nativeFbUser') {
        // Native auth is now handled by Supabase - the native app should send
        // a Supabase session instead. For now, log and skip.
        console.log('received nativeFbUser (deprecated, use Supabase auth)')
      } else if (type === 'pushNotificationPermissionStatus') {
        const { status } =
          data as MesageTypeMap['pushNotificationPermissionStatus']
        await handlePushNotificationPermissionStatus(status)
      } else if (type === 'pushToken') {
        const { token } = data as MesageTypeMap['pushToken']
        if (token !== privateUser?.pushToken) {
          await setPushToken(token)
        }
      } else if (type === 'notification') {
        const notification = data as Notification
        // TODO: mark the notification as seen
        const sourceUrl = getSourceUrl(notification)
        console.log('sourceUrl', sourceUrl)
        try {
          router.push(sourceUrl)
        } catch (e) {
          console.log(`Error navigating to notification route ${sourceUrl}`, e)
        }
      } else if (type === 'link') {
        const { url } = data as MesageTypeMap['link']
        const newRoute = url.startsWith('/') ? url : '/' + url
        console.log('Received link from native, current route', router.asPath)
        if (router.asPath === newRoute) return
        console.log('Navigating to link from native:', newRoute)
        try {
          await router.push(newRoute)
        } catch (e) {
          console.log(`Error navigating to linked route`, e)
        }
      } else if (type === 'hasReviewAction') {
        const { hasAction, isAvailable } =
          data as MesageTypeMap['hasReviewAction']
        if (hasAction && isAvailable) {
          console.log('Store review is available, requesting review.')
          postMessageToNative('storeReviewRequested', {})
          // Update the user's last review time optimistically
          api('me/private/update', { lastAppReviewTime: Date.now() }).catch(
            (e) => {
              console.error('Failed to update lastAppReviewTime', e)
            }
          )
        } else {
          console.log('Store review not available or action already taken.', {
            hasAction,
            isAvailable,
          })
        }
      }
    }
  )

  useNativeMessages(
    [
      'nativeFbUser',
      'pushNotificationPermissionStatus',
      'pushToken',
      'notification',
      'link',
      'hasReviewAction',
    ],
    handleNativeMessage
  )

  const isIOS = platform === 'ios' && isNative
  return (
    <NativeContext.Provider value={{ isNative, platform, version, isIOS }}>
      {children}
    </NativeContext.Provider>
  )
}

export const useNativeInfo = () => {
  const context = useContext(NativeContext)
  if (context === undefined) {
    throw new Error('useNativeInfo must be used within a NativeMessageListener')
  }
  return context
}

export const useIsNativeIOS = () => {
  const { isNative, platform } = useNativeInfo()
  return isNative && platform === 'ios'
}
